/**
 * Photo Carousel Component for Re:Nav-96
 *
 * Displays a rotating carousel of approved photos from the gallery.
 * Fetches random photos from the API and auto-rotates them.
 *
 * Features:
 * - Auto-rotating photo display
 * - Shows photo thumbnails with navigation dots
 * - Pause on hover
 * - Manual navigation with arrows
 * - Graceful loading and error states
 * - Links to full gallery
 *
 * @example
 * ```tsx
 * <PhotoCarousel />
 * <PhotoCarousel limit={8} interval={5000} />
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router';
import { photosApi } from '@/services/api';
import type { Photo } from '@/types';
import './PhotoCarousel.css';

/**
 * Props for the PhotoCarousel component
 */
interface PhotoCarouselProps {
  /** Number of photos to display in rotation */
  limit?: number;
  /** Auto-rotate interval in milliseconds */
  interval?: number;
  /** Optional CSS class name for container */
  className?: string;
}

/**
 * Loading skeleton for the carousel
 */
function CarouselLoadingSkeleton() {
  return (
    <div className="carousel-loading">
      <div className="carousel-loading-image" />
      <div className="carousel-loading-dots">
        <div className="carousel-loading-dot" />
        <div className="carousel-loading-dot" />
        <div className="carousel-loading-dot" />
      </div>
    </div>
  );
}

/**
 * Empty state when no photos are available
 */
function CarouselEmptyState() {
  return (
    <div className="carousel-empty">
      <svg
        className="w-12 h-12 text-white/20 mb-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
      <span className="text-white/50 text-sm">
        No photos yet. Be the first to share!
      </span>
    </div>
  );
}

/**
 * Navigation arrow button
 */
interface NavButtonProps {
  direction: 'prev' | 'next';
  onClick: () => void;
}

function NavButton({ direction, onClick }: NavButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`carousel-nav carousel-nav-${direction}`}
      aria-label={direction === 'prev' ? 'Previous photo' : 'Next photo'}
    >
      <svg
        className="w-5 h-5 text-white"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        {direction === 'prev' ? (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        ) : (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        )}
      </svg>
    </button>
  );
}

/**
 * PhotoCarousel Component
 *
 * Displays a rotating carousel of photos from the gallery.
 * Automatically cycles through photos with configurable timing.
 *
 * @param props - Component props
 * @returns The photo carousel component
 *
 * @example
 * // Basic usage
 * <PhotoCarousel />
 *
 * // With custom settings
 * <PhotoCarousel limit={10} interval={4000} />
 */
export function PhotoCarousel({
  limit = 6,
  interval = 5000,
  className = '',
}: PhotoCarouselProps) {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch random photos
  useEffect(() => {
    let mounted = true;

    async function fetchPhotos() {
      try {
        setIsLoading(true);
        setError(null);
        const data = await photosApi.getRandom({ limit });
        if (mounted) {
          setPhotos(data);
          setCurrentIndex(0);
        }
      } catch (err) {
        if (mounted) {
          setError('Failed to load photos');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    fetchPhotos();

    // Refresh photos every 10 minutes
    const refreshId = setInterval(fetchPhotos, 600000);

    return () => {
      mounted = false;
      clearInterval(refreshId);
    };
  }, [limit]);

  // Go to next photo
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  }, [photos.length]);

  // Go to previous photo
  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  }, [photos.length]);

  // Go to specific photo
  const goToIndex = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Auto-rotate effect
  useEffect(() => {
    if (isPaused || photos.length <= 1) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    intervalRef.current = setInterval(goToNext, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPaused, photos.length, interval, goToNext]);

  // Handle hover pause
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  // Get current photo
  const currentPhoto = photos[currentIndex];

  return (
    <div className={`carousel-container card ${className}`}>
      {/* Header */}
      <div className="carousel-header">
        <div className="flex items-center gap-2">
          <svg
            className="w-5 h-5 text-primary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <h3 className="text-lg font-display font-semibold text-white">
            Photo Gallery
          </h3>
        </div>
        <Link
          to="/member/photos"
          className="text-primary text-sm font-medium hover:text-primary-light transition-colors inline-flex items-center gap-1"
        >
          View All
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>

      {/* Carousel content */}
      <div
        className="carousel-wrapper"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {isLoading ? (
          <CarouselLoadingSkeleton />
        ) : error ? (
          <div className="carousel-error">
            <span className="text-red-400 text-sm">{error}</span>
          </div>
        ) : photos.length === 0 ? (
          <CarouselEmptyState />
        ) : (
          <>
            {/* Main image display */}
            <div className="carousel-image-container">
              <img
                src={currentPhoto.thumbnailUrl || currentPhoto.blobUrl}
                alt={currentPhoto.originalFilename || 'Gallery photo'}
                className="carousel-image"
              />

              {/* Photo info overlay */}
              <div className="carousel-overlay">
                <span className="carousel-uploader">
                  {currentPhoto.uploaderName || 'Member'}
                </span>
              </div>

              {/* Navigation arrows */}
              {photos.length > 1 && (
                <>
                  <NavButton direction="prev" onClick={goToPrev} />
                  <NavButton direction="next" onClick={goToNext} />
                </>
              )}
            </div>

            {/* Navigation dots */}
            {photos.length > 1 && (
              <div className="carousel-dots">
                {photos.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
                    onClick={() => goToIndex(index)}
                    aria-label={`Go to photo ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Auto-rotate indicator */}
      {!isLoading && !error && photos.length > 1 && (
        <div className="carousel-indicator">
          <span className={`carousel-indicator-dot ${isPaused ? 'paused' : ''}`} />
          <span className="text-white/40 text-xs">
            {isPaused ? 'Paused' : 'Auto-rotating'}
          </span>
        </div>
      )}
    </div>
  );
}

export default PhotoCarousel;
